import {
  ActionIcon,
  Button,
  Group,
  Indicator,
  Menu,
  ScrollArea,
  Stack,
  Switch,
  Text,
  useMantineColorScheme,
} from '@mantine/core';
import { IconBell, IconCheck, IconList } from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector, useAppDispatch } from '@store';
import { updateNotificationReadStatus } from '@store/slices/notificationsSlice';
import routes from '@/commons/routes';
import { useUser } from '@contexts/UserContext';
import {
  getNotificationSoundEnabled,
  setNotificationSoundEnabled,
  onSoundPreferenceChange,
} from '@/commons/notificationSound';
import { Notification as NotificationType } from './types';
import { useTimeAgo } from '@/commons/timeAgo';
import { getNotificationColor } from '@/commons/notificationColor';
import {
  FilterSelectionModal,
  findMatchingFilters,
} from './FilterSelectionModal';
import { fetchFolderCategoryForNotification } from './fetchFolderCategory';
import { buildQueryFoldersSearchFromNotification } from '@/users/components/UserFolders/utils/queryFoldersState';

export const NotificationCenter = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useUser();
  const username = user?.username || '';
  const { colorScheme } = useMantineColorScheme();
  const timeAgo = useTimeAgo();

  // Get notifications from Redux store
  const serverNotifications: NotificationType[] = useAppSelector(
    (state) =>
      state.notifications.notifications as unknown as NotificationType[]
  );

  const unreadCount = serverNotifications.filter((n) => !n.read).length;
  const [soundEnabled, setSoundEnabled] = useState(() =>
    getNotificationSoundEnabled()
  );
  const [opened, setOpened] = useState(false);
  const [filterModalOpened, setFilterModalOpened] = useState(false);
  const [filterModalCategoryHint, setFilterModalCategoryHint] = useState<
    string | undefined
  >(undefined);
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationType | null>(null);

  // Listen for sound preference changes from other tabs
  useEffect(() => {
    const unsubscribe = onSoundPreferenceChange((enabled) => {
      setSoundEnabled(enabled);
    });

    return unsubscribe;
  }, []);

  const handleSoundToggle = (checked: boolean) => {
    setSoundEnabled(checked);
    setNotificationSoundEnabled(checked);
  };

  const filters = useAppSelector((state) => state.filters.filters);

  const handleOpenFilter = useCallback(
    (filterId: string, folderName: string) => {
      const filterDef = filters.find((f: any) => f.id === filterId);
      if (!username) {
        return;
      }
      const base = routes.routes.queryFolders.view(username, filterId);
      const qs = buildQueryFoldersSearchFromNotification(filterDef, folderName);
      navigate(qs ? `${base}?${qs}` : base);
      setOpened(false);
    },
    [filters, username, navigate]
  );

  return (
    <Menu
      position="bottom-end"
      width={400}
      closeOnItemClick={false}
      opened={opened}
      onChange={setOpened}
    >
      <Menu.Target>
        <Indicator
          mb={-2}
          inline
          label={unreadCount > 0 ? unreadCount : null}
          size={18}
          color="red"
          disabled={unreadCount === 0}
        >
          <ActionIcon variant="default" size="md">
            <IconBell size={18} stroke={1.5} />
          </ActionIcon>
        </Indicator>
      </Menu.Target>

      <Menu.Dropdown>
        <Group justify="space-between" px="xs" py="xs">
          <Text size="sm" fw={600}>
            {intl.formatMessage({ id: 'notifications' })}
          </Text>
          {serverNotifications.length > 0 && unreadCount > 0 && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconCheck size={14} />}
              onClick={() => {
                // Mark all unread notifications as read
                const unreadNotifications = serverNotifications.filter(
                  (n) => !n.read
                );
                unreadNotifications.forEach((n) => {
                  dispatch(
                    updateNotificationReadStatus({
                      notificationId: n.id,
                      read: true,
                    })
                  );
                });
              }}
            >
              {intl.formatMessage({ id: 'markAllAsRead' })}
            </Button>
          )}
        </Group>

        <Menu.Divider />

        <Group justify="space-between" px="xs" py="xs">
          <Text size="sm">
            {intl.formatMessage({ id: 'notificationSound' })}
          </Text>
          <Switch
            checked={soundEnabled}
            onChange={(event) => handleSoundToggle(event.currentTarget.checked)}
            size="sm"
          />
        </Group>

        <Menu.Divider />

        {serverNotifications.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {intl.formatMessage({ id: 'noNotifications' })}
          </Text>
        ) : (
          <ScrollArea h={400}>
            <Stack gap="xs">
              {serverNotifications.map((notification: NotificationType) => (
                <Menu.Item
                  key={notification.id}
                  onClick={() => {
                    if (!notification.read) {
                      dispatch(
                        updateNotificationReadStatus({
                          notificationId: notification.id,
                          read: true,
                        })
                      );
                    }
                    const folderName = notification.data?.folderName;
                    if (!folderName) {
                      return;
                    }
                    setOpened(false);

                    void (async () => {
                      try {
                        const category =
                          await fetchFolderCategoryForNotification(folderName);
                        const availableFilters = findMatchingFilters(
                          filters,
                          folderName,
                          { category }
                        );
                        if (availableFilters.length === 1) {
                          handleOpenFilter(availableFilters[0].id, folderName);
                          return;
                        }
                        setFilterModalCategoryHint(category);
                        setSelectedNotification(notification);
                        setFilterModalOpened(true);
                      } catch {
                        setFilterModalCategoryHint(undefined);
                        setSelectedNotification(notification);
                        setFilterModalOpened(true);
                      }
                    })();
                  }}
                  style={{
                    opacity: notification.read ? 0.6 : 1,
                    backgroundColor: notification.read
                      ? 'transparent'
                      : colorScheme === 'dark'
                        ? 'var(--mantine-color-dark-6)'
                        : 'var(--mantine-color-gray-0)',
                  }}
                >
                  <Group gap="sm" wrap="nowrap" align="stretch">
                    <div
                      style={{
                        width: 4,
                        backgroundColor: `var(--mantine-color-${getNotificationColor(notification.type)}-6)`,
                        borderRadius: 2,
                        flexShrink: 0,
                      }}
                    />
                    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                      <Group
                        justify="space-between"
                        align="flex-start"
                        gap="xs"
                        wrap="nowrap"
                      >
                        <Text
                          size="sm"
                          fw={500}
                          lineClamp={1}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          {intl.formatMessage({
                            id: `notification.${notification.type}`,
                          })}
                        </Text>
                        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                          {timeAgo.format(new Date(notification.createdAt))}
                        </Text>
                      </Group>
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {intl.formatMessage(
                          { id: `notification.${notification.i18nKey}` },
                          notification.data || {}
                        )}
                        .
                      </Text>
                    </Stack>
                  </Group>
                </Menu.Item>
              ))}
            </Stack>
          </ScrollArea>
        )}

        {serverNotifications.length > 0 && (
          <>
            <Menu.Divider />
            <Group px="xs" py="xs">
              <Button
                variant="light"
                fullWidth
                leftSection={<IconList size={16} />}
                onClick={() => {
                  setOpened(false);
                  if (username) {
                    navigate(routes.routes.notifications(username));
                  }
                }}
              >
                {intl.formatMessage({ id: 'viewAllNotifications' })}
              </Button>
            </Group>
          </>
        )}
      </Menu.Dropdown>

      {selectedNotification && (
        <FilterSelectionModal
          opened={filterModalOpened}
          onClose={() => {
            setFilterModalOpened(false);
            setSelectedNotification(null);
            setFilterModalCategoryHint(undefined);
          }}
          folderName={selectedNotification.data?.folderName || ''}
          categoryHint={filterModalCategoryHint}
        />
      )}
    </Menu>
  );
};
